import React, { useEffect, useState } from 'react';
import { Collapse } from 'react-collapse';
import Icon from './Icon';
import clsx from 'clsx';
import { uniqueId } from 'lodash-es';

interface CollapsibleAttributes {
  label?: string | JSX.Element;
  className?: string;
  labelClassName?: string;
  contentClassName?: string;
  transitionDuration?: string;
  onStateChange?: Function;
  isOpen?: boolean;
  hideBorder?: boolean;
  children?: any;
  labelSize?: string;
  id?: string;
}

const Collapsible = ({
  children,
  label,
  className = '',
  transitionDuration = '0.2s',
  onStateChange: externalStateChange,
  isOpen: externalIsOpen,
  hideBorder,
  labelClassName = '',
  contentClassName = '',
  labelSize = '',
  id,
}: CollapsibleAttributes) => {
  const [isOpen, setIsOpen] = useState<boolean>(externalIsOpen ?? false);
  const contentRef = React.useRef<HTMLDivElement>(null);

  const uuid = uniqueId();
  const actualId = id ?? uuid;

  const onStateChange = (open: boolean) => {
    externalStateChange && externalStateChange(open);
    setIsOpen(open);
  };

  useEffect(() => {
    setIsOpen(externalIsOpen ?? false);
  }, [externalIsOpen]);

  useEffect(() => {
    if (contentRef.current) {
      const focusableElements =
        contentRef.current.querySelectorAll<HTMLElement>(
          'a, button, input, textarea, select, [tabindex]'
        );

      focusableElements.forEach((element) => {
        element.setAttribute('tabindex', isOpen ? '0' : '-1');
      });
    }
  }, [isOpen]);

  return (
    <>
      <div
        className={clsx(
          'grid border-b',
          className,
          hideBorder ? 'collapsible collapsible--no-border' : ''
        )}
      >
        <button
          className={clsx(
            'flex items-center justify-between border-t py-3 text-lg',
            labelClassName
          )}
          aria-expanded={isOpen}
          onClick={() => onStateChange(!isOpen)}
          id={actualId}
          aria-controls={`accordion-${actualId}`}
        >
          <span className="flex flex-1 text-gray-900 ">{label}</span>
          <Icon
            className={clsx(
              'ml-4 h-4 text-gray-900 transition-all',
              isOpen ? 'rotate-180' : 'rotate-0',
              labelSize ?? 'h-5 w-5'
            )}
            style={{ transitionDuration: transitionDuration }}
            name="caret-down"
          />
        </button>
        <div
          className={clsx(
            'grid transition-[grid-template-rows] duration-200',
            isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
          )}
          id={`accordion-${actualId}`}
          aria-hidden={!isOpen}
          ref={contentRef}
        >
          <div
            className={clsx(
              'overflow-hidden text-gray-900',
              contentClassName
            )}
          >
            {children}
          </div>
        </div>
      </div>
    </>
  );
};

export default Collapsible;
